import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import { Validator } from './ValidationResult';
import { makeLabel } from '../utils/generalUtils';
import { useAutofillNotifier } from '../hooks/autofillHook';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import HyperLink from './HyperLink';

export const customCtrlDef = {
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "number", min: 1, step: 1, field: "maxlength", label: t('maxLength') }
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, href, ...props }) => <PhoneInput maxLength={def.maxlength} href={href ?? def.href} {...props}></PhoneInput>
};

/**
 * Input textbox for recoding telephone numbers.
 * @component
 */
const PhoneInput = withComponentCapabilities(({value, live = false, onChange, placeholder, required = false, hideLabel = false, readonly = false, disabled = false, href, ...props}) => {
    const [text, setText] = useState(value ?? '');
    const [validity, setValidity] = useState();
    const { createAutofillProps } = useAutofillNotifier();
    const ctrlRef = useRef();

    useEffect(() => {
        setText(value ?? '')
        setValidity();
    }, [value]);

    const handleUpdate = useCallback(newText => {
        const valid = ctrlRef.current.checkValidity();
        setValidity(valid)
        if (valid && (newText ?? '') !== (value ?? '')) {
            onChange(newText);
        }
    }, [onChange, value]);

    const autofillProps = useMemo(() =>
        createAutofillProps((el) => {
            const newText = el.value;
            let shouldUpdate = false;

            setText(prev => {
                shouldUpdate = newText && prev !== newText;
                return newText;
            });

            if (shouldUpdate && (!required || newText)) {
                handleUpdate(newText);
                return true;
            }
            return shouldUpdate;
        }, ctrlRef)
    , [createAutofillProps, handleUpdate, required, value]);

    const handleChange = useCallback(e => {
        const newText = e.target.value
        setText(newText);
        if (live || document.activeElement !== e.target) {
            handleUpdate(newText)
        }
    }, [handleUpdate, live]);

    if (readonly) {
        return <FloatingLabel placeholder={placeholder} hideLabel={hideLabel} value={value} href={href}>{value && <HyperLink href={`tel:${value}`}>{value}</HyperLink>}</FloatingLabel>;
    }

    return <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={text} href={href}>
        <Validator value={value} ref={ctrlRef} onValidation={setValidity}>
            <FormControl type="tel" {...autofillProps} isInvalid={validity === false} value={text} onChange={e => handleChange(e)} onBlur={() => handleUpdate(text)} placeholder={makeLabel(placeholder, required)} required={required} disabled={disabled} onInvalid={() => setValidity(false)} {...props}></FormControl>
        </Validator>
    </FloatingLabel>
}, {

});

PhoneInput.propTypes = {
    /**
     * The phone number to be displayed by the control
     */
    value: PropTypes.string,
    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,
    /**
     * Function to call when the input value has been changed
     */
    onChange: PropTypes.func,
    /**
     * The label for the control
     */
    placeholder: PropTypes.string,
    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,
    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
    /**
     * The maximum number of characters that can be entered into this control
     */
    maxLength: PropTypes.number,
    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default PhoneInput;